/**
 * Everything about the shape of the window: safe areas, the on-screen keyboard, orientation,
 * and the scroll behaviour an instrument surface is not allowed to have.
 *
 * The unifying problem is that on iOS the *layout* viewport and the *visual* viewport are
 * different rectangles, and CSS can only see the first one. `100dvh` is a lie while the
 * keyboard is up; `env(safe-area-inset-bottom)` does not change when a text field pushes the
 * page around; and a rotation leaves WebKit reporting the previous orientation's insets for
 * a frame. Each of those is handled here once, so no screen has to rediscover it.
 *
 * Observers attach at import. There is exactly one window, the app cannot function without
 * knowing its shape, and deferring this would mean rendering a first frame with the wrong
 * layout and then correcting it.
 */

import { computed, signal, type ReadonlySignal } from '../core/signal';
import { schedule } from '../core/dom';

export type Orientation = 'portrait' | 'landscape';
export type SizeClass = 'compact' | 'regular';

export interface LayoutMetrics {
  /** Visual viewport width in CSS pixels — what the user can actually see. */
  readonly width: number;
  readonly height: number;
  readonly orientation: Orientation;
  readonly widthClass: SizeClass;
  readonly heightClass: SizeClass;
  readonly safeTop: number;
  readonly safeRight: number;
  readonly safeBottom: number;
  readonly safeLeft: number;
  /** Layout-viewport pixels hidden behind the on-screen keyboard. */
  readonly keyboardInset: number;
  readonly devicePixelRatio: number;
}

/**
 * Below this, a phone in portrait: one column of controls, the board takes the width.
 * Above it there is room for the board and an inspector side by side.
 */
const COMPACT_WIDTH = 480;
/** Below this there is not enough height for a toolbar, a full board and a transport. */
const COMPACT_HEIGHT = 500;

/**
 * A visual viewport smaller than the layout viewport by less than this is a pinch-zoom or a
 * toolbar, not a keyboard. Chosen well under the height of any iOS keyboard and well over
 * the height of Safari's collapsing address bar.
 */
const KEYBOARD_THRESHOLD = 90;

function classify(value: number, breakpoint: number): SizeClass {
  return value < breakpoint ? 'compact' : 'regular';
}

// MARK: - Safe-area measurement
//
// `env()` is only readable from CSS, so the values are round-tripped through a hidden element
// whose padding is set to the four insets. The element is created once and never removed —
// re-creating it per measurement would cost a style recalculation on every rotation.

let probeElement: HTMLElement | null = null;

function safeAreaProbe(): HTMLElement {
  if (probeElement !== null) return probeElement;
  const probe = document.createElement('div');
  // Written through the CSSOM rather than a style attribute, which is what lets the page
  // ship a Content-Security-Policy with no 'unsafe-inline'.
  const style = probe.style;
  style.setProperty('position', 'fixed');
  style.setProperty('top', '0');
  style.setProperty('left', '0');
  style.setProperty('width', '0');
  style.setProperty('height', '0');
  style.setProperty('visibility', 'hidden');
  style.setProperty('pointer-events', 'none');
  style.setProperty('padding-top', 'env(safe-area-inset-top, 0px)');
  style.setProperty('padding-right', 'env(safe-area-inset-right, 0px)');
  style.setProperty('padding-bottom', 'env(safe-area-inset-bottom, 0px)');
  style.setProperty('padding-left', 'env(safe-area-inset-left, 0px)');
  probe.setAttribute('aria-hidden', 'true');
  (document.body ?? document.documentElement).appendChild(probe);
  probeElement = probe;
  return probe;
}

interface SafeArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

function measureSafeArea(): SafeArea {
  const computedStyle = window.getComputedStyle(safeAreaProbe());
  const read = (value: string): number => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return {
    top: read(computedStyle.paddingTop),
    right: read(computedStyle.paddingRight),
    bottom: read(computedStyle.paddingBottom),
    left: read(computedStyle.paddingLeft),
  };
}

// MARK: - viewport-fit=cover

/**
 * Without `viewport-fit=cover` the four `env(safe-area-inset-*)` values are all zero and the
 * app is letterboxed inside the safe rectangle instead of painting under the status area.
 * index.html sets it; this repairs any entry point that does not — a test harness, a page
 * opened from an old bookmark, or a future embed.
 */
function ensureViewportFitCover(): void {
  const meta =
    document.querySelector<HTMLMetaElement>('meta[name="viewport"]') ??
    (() => {
      const created = document.createElement('meta');
      created.name = 'viewport';
      created.content = 'width=device-width, initial-scale=1';
      document.head.appendChild(created);
      return created;
    })();

  if (/viewport-fit\s*=\s*cover/i.test(meta.content)) return;
  meta.content = `${meta.content.replace(/,\s*viewport-fit\s*=\s*\w+/i, '')}, viewport-fit=cover`;
}

// MARK: - State

function readOrientation(width: number, height: number): Orientation {
  return width > height ? 'landscape' : 'portrait';
}

function initialMetrics(): LayoutMetrics {
  const width = window.visualViewport?.width ?? window.innerWidth;
  const height = window.visualViewport?.height ?? window.innerHeight;
  const safe = { top: 0, right: 0, bottom: 0, left: 0 };
  return {
    width,
    height,
    orientation: readOrientation(width, height),
    widthClass: classify(width, COMPACT_WIDTH),
    heightClass: classify(height, COMPACT_HEIGHT),
    safeTop: safe.top,
    safeRight: safe.right,
    safeBottom: safe.bottom,
    safeLeft: safe.left,
    keyboardInset: 0,
    devicePixelRatio: window.devicePixelRatio,
  };
}

const layoutSignal = signal<LayoutMetrics>(initialMetrics());

/** The live shape of the window. Re-emits only when a value actually changes. */
export const layout: ReadonlySignal<LayoutMetrics> = layoutSignal;

export const orientation: ReadonlySignal<Orientation> = computed(
  () => layoutSignal.value.orientation,
);

export const keyboardVisible: ReadonlySignal<boolean> = computed(
  () => layoutSignal.value.keyboardInset > 0,
);

function sameMetrics(a: LayoutMetrics, b: LayoutMetrics): boolean {
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.safeTop === b.safeTop &&
    a.safeRight === b.safeRight &&
    a.safeBottom === b.safeBottom &&
    a.safeLeft === b.safeLeft &&
    a.keyboardInset === b.keyboardInset &&
    a.devicePixelRatio === b.devicePixelRatio
  );
}

function currentKeyboardInset(): number {
  const visual = window.visualViewport;
  if (visual === null) return 0;
  // A pinch-zoom also shrinks the visual viewport. Only an un-zoomed shrink is a keyboard.
  if (visual.scale > 1.02) return 0;
  const hidden = window.innerHeight - visual.height - visual.offsetTop;
  return hidden > KEYBOARD_THRESHOLD ? Math.round(hidden) : 0;
}

/**
 * False until the CSS custom properties have been written once. Without it, a device with no
 * safe areas would produce a first measurement identical to the initial signal value, the
 * change check would short-circuit, and `--viewport-h` would never be written at all.
 */
let propertiesWritten = false;

function measure(): void {
  const visual = window.visualViewport;
  const width = Math.round(visual?.width ?? window.innerWidth);
  const height = Math.round(visual?.height ?? window.innerHeight);
  const safe = measureSafeArea();
  const keyboardInset = currentKeyboardInset();

  const next: LayoutMetrics = {
    width,
    height,
    orientation: readOrientation(width, height),
    widthClass: classify(width, COMPACT_WIDTH),
    heightClass: classify(height, COMPACT_HEIGHT),
    safeTop: safe.top,
    safeRight: safe.right,
    safeBottom: safe.bottom,
    safeLeft: safe.left,
    keyboardInset,
    devicePixelRatio: window.devicePixelRatio,
  };

  if (propertiesWritten && sameMetrics(next, layoutSignal.peek())) return;
  propertiesWritten = true;
  layoutSignal.value = next;
  writeCustomProperties(next);
  liftRegisteredElements(next);
}

/**
 * Mirrors the measured shape into CSS.
 *
 * `--viewport-h` exists because `100dvh` does not shrink for the keyboard on iOS, so a sheet
 * sized in `dvh` ends up behind it. `--keyboard-inset` is what `.safe-bottom` adds to the
 * safe area. The `--safe-*-px` values are resolved mirrors for code that needs a number
 * rather than an `env()` reference — tokens.css keeps the live `env()` values as the styling
 * authority, so these can never shadow it.
 */
function writeCustomProperties(metrics: LayoutMetrics): void {
  const style = document.documentElement.style;
  style.setProperty('--viewport-h', `${metrics.height}px`);
  style.setProperty('--viewport-w', `${metrics.width}px`);
  style.setProperty('--keyboard-inset', `${metrics.keyboardInset}px`);
  style.setProperty('--safe-top-px', `${metrics.safeTop}px`);
  style.setProperty('--safe-right-px', `${metrics.safeRight}px`);
  style.setProperty('--safe-bottom-px', `${metrics.safeBottom}px`);
  style.setProperty('--safe-left-px', `${metrics.safeLeft}px`);
  // An attribute rather than a media query so layout can key off the *visual* viewport,
  // which is the one that shrinks when the keyboard appears.
  document.documentElement.dataset['orientation'] = metrics.orientation;
  document.documentElement.dataset['keyboard'] = metrics.keyboardInset > 0 ? 'visible' : 'hidden';
}

// MARK: - Keeping fields and sheets above the keyboard

interface LiftedElement {
  readonly element: HTMLElement;
  /** Gap kept between the element's bottom edge and the top of the keyboard. */
  readonly margin: number;
}

const lifted = new Set<LiftedElement>();

/**
 * Keeps `element` fully visible when the on-screen keyboard appears.
 *
 * `scrollIntoView` is not enough on iOS: the page under a fixed-position sheet does not
 * scroll, so the sheet stays exactly where it was, behind the keyboard. Translating the
 * element by the measured overlap is the only approach that works for both a fixed sheet and
 * an in-flow field.
 *
 * This owns the element's `transform` for as long as it is registered — give it a wrapper if
 * the element also animates. Returns a disposer.
 */
export function keepAboveKeyboard(element: HTMLElement, margin = 12): () => void {
  const entry: LiftedElement = { element, margin };
  lifted.add(entry);
  liftElement(entry, layoutSignal.peek());
  return () => {
    lifted.delete(entry);
    element.style.removeProperty('transform');
  };
}

function liftRegisteredElements(metrics: LayoutMetrics): void {
  if (lifted.size === 0) return;
  // Measuring in a frame callback keeps the reads out of the resize handler, where they
  // would force a synchronous layout on every keyboard animation step.
  schedule(() => {
    for (const entry of lifted) liftElement(entry, metrics);
  });
}

function liftElement(entry: LiftedElement, metrics: LayoutMetrics): void {
  const style = entry.element.style;
  if (metrics.keyboardInset === 0) {
    style.removeProperty('transform');
    return;
  }

  // Reset before measuring: a stale translate would make the element look already-lifted.
  style.removeProperty('transform');
  const rect = entry.element.getBoundingClientRect();
  const visibleBottom = window.innerHeight - metrics.keyboardInset;
  const overlap = rect.bottom + entry.margin - visibleBottom;
  if (overlap <= 0) return;
  style.setProperty('transform', `translate3d(0, ${-Math.round(overlap)}px, 0)`);
}

/**
 * Brings the focused field into view after the keyboard has finished animating.
 *
 * iOS fires `focusin` before the keyboard exists, so the useful measurement only arrives
 * with the `visualViewport` resize that follows. This waits for that resize, with a timeout
 * so a browser that never sends one still does something sensible.
 */
export function revealFocusedField(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  // A field inside a registered sheet is already handled by that sheet's lift; scrolling it
  // as well would fight the translate and leave the sheet half off screen.
  for (const entry of lifted) {
    if (entry.element.contains(active)) return;
  }

  const reveal = (): void => {
    const inset = currentKeyboardInset();
    const rect = active.getBoundingClientRect();
    const visibleBottom = window.innerHeight - inset;
    if (rect.bottom <= visibleBottom && rect.top >= 0) return;
    active.scrollIntoView({ block: 'center', behavior: 'auto' });
  };

  const visual = window.visualViewport;
  if (visual === null) {
    schedule(reveal);
    return;
  }

  let settled = false;
  const onResize = (): void => {
    if (settled) return;
    settled = true;
    visual.removeEventListener('resize', onResize);
    window.clearTimeout(timer);
    schedule(reveal);
  };
  const timer = window.setTimeout(onResize, 350);
  visual.addEventListener('resize', onResize);
}

// MARK: - Instrument surfaces

/**
 * Hands every browser gesture on `element` back to the app.
 *
 * `touch-action: none` (applied by the `.instrument` class in tokens.css) already stops the
 * browser from scrolling, zooming or triggering pull-to-refresh on this element. The
 * non-passive `touchmove` guard is the belt to that pair of braces: WebKit has shipped
 * versions where a two-finger gesture that begins on a `touch-action: none` element still
 * scrolls an ancestor, and one accidental pull-to-refresh during a take destroys a recording.
 *
 * Scrollers nested inside the surface opt back in by carrying `.scroll-y` or `.scroll-x`;
 * their gestures are left alone. Returns a disposer.
 */
export function lockInstrumentSurface(element: HTMLElement): () => void {
  element.classList.add('instrument');
  element.style.setProperty('overscroll-behavior', 'none');

  const onTouchMove = (event: TouchEvent): void => {
    if (event.cancelable === false) return;
    const target = event.target;
    if (target instanceof Element && target.closest('.scroll-y, .scroll-x') !== null) return;
    event.preventDefault();
  };

  // Must be non-passive or preventDefault is ignored.
  element.addEventListener('touchmove', onTouchMove, { passive: false });

  // iOS shows the callout menu and the text-selection loupe on a long press even when
  // touch-action is none; the CSS in tokens.css suppresses it, and this stops the
  // contextmenu event a trackpad or a hardware keyboard would still deliver.
  const onContextMenu = (event: Event): void => event.preventDefault();
  element.addEventListener('contextmenu', onContextMenu);

  const onDragStart = (event: Event): void => event.preventDefault();
  element.addEventListener('dragstart', onDragStart);

  return () => {
    element.classList.remove('instrument');
    element.style.removeProperty('overscroll-behavior');
    element.removeEventListener('touchmove', onTouchMove);
    element.removeEventListener('contextmenu', onContextMenu);
    element.removeEventListener('dragstart', onDragStart);
  };
}

// MARK: - Wiring

function install(): () => void {
  ensureViewportFitCover();
  measure();

  const disposers: Array<() => void> = [];
  const remeasure = (): void => measure();

  const visual = window.visualViewport;
  if (visual !== null) {
    visual.addEventListener('resize', remeasure);
    visual.addEventListener('scroll', remeasure);
    disposers.push(() => {
      visual.removeEventListener('resize', remeasure);
      visual.removeEventListener('scroll', remeasure);
    });
  }

  window.addEventListener('resize', remeasure);
  disposers.push(() => window.removeEventListener('resize', remeasure));

  /**
   * WebKit reports the *previous* orientation's safe-area insets for a frame or two after a
   * rotation, so a single measurement here would leave a notch-sized gap on screen until the
   * next unrelated resize. Re-measuring across the following frames costs three style reads
   * and removes the artefact entirely.
   */
  const onOrientationChange = (): void => {
    measure();
    schedule(() => {
      measure();
      schedule(measure);
    });
    window.setTimeout(measure, 300);
  };
  window.addEventListener('orientationchange', onOrientationChange);
  disposers.push(() => window.removeEventListener('orientationchange', onOrientationChange));

  const onFocusIn = (event: FocusEvent): void => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      revealFocusedField();
    }
  };
  document.addEventListener('focusin', onFocusIn);
  disposers.push(() => document.removeEventListener('focusin', onFocusIn));

  if (typeof ResizeObserver === 'function') {
    // The visual viewport does not resize when a hardware keyboard is attached or when the
    // window is resized on iPadOS in Split View; the document element does.
    const observer = new ResizeObserver(remeasure);
    observer.observe(document.documentElement);
    disposers.push(() => observer.disconnect());
  }

  return () => {
    for (const dispose of disposers) dispose();
    probeElement?.remove();
    probeElement = null;
  };
}

/** Disposer for the window-level listeners. Exported for tests that mount several apps. */
export const stopViewportHandling = install();
